// Global Instructions Rule Applied!
//
// The waterfall tier grid: one row per tier with the house inputs in the
// cells (tier type, stop metric, stop base, tier group, threshold source and
// figure, whose receipts the stop counts, the GP share and its percent, the
// pools a Cross-Pool Loss Offset tier offsets against, the reference), and
// under it each tier read back the way the agreement states it.
//
// Shared by the two places a waterfall is edited: a fund's Terms tab
// (WaterfallSection - the fund's own waterfall, with its pools and the fund's
// Carried Interest defaults printed beside each sourced figure) and
// Configuration → Waterfall Templates (WaterfallTemplatesPanel - the shapes a
// fund's waterfall is created from). The two keep their own header fields
// and their own save; the rows, the lookups and the payload the tiers become
// are the one code here, so the two cannot drift.
//
// Each tier is two splits: the GP's (rate source + the figure for a fixed
// rate; "No GP share" leaves the GP out) and the LP's, always the remainder.

import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, Input, InputNumber, Select, Table, Tooltip, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import HelpTip from '../../../../../core/components/HelpTip.tsx'
import { decimalToPercent, percentToDecimal } from '../../../../../core/utils/inputNumberFormat.ts'
import type { FundCarryDefaults } from '../../../../../core/utils/fundCarryDefaults.ts'
import { lookupOptionsHelp } from '../../../utils/lookupOptionsHelp.tsx'
import { describeTierTerms } from './waterfallTierWording.ts'
import type { WaterfallSplitPayload, WaterfallTierPayload } from '../../../../../state_manager/data_models/fund_waterfall_record.ts'
import type {
  WaterfallBasis,
  WaterfallHistoryCheck,
  WaterfallLookup,
  WaterfallRateSource,
  WaterfallTemplate
} from '../../../../../state_manager/data_models/waterfall_lookups_model.ts'

const { Text } = Typography

// lookup rows the grid keys behaviour on (labels of 20260714140000, codes of 20270223120200)
export const LP_CLASS = 'LP'
export const GP_CLASS = 'GP'
export const METRIC_NONE = 'None'
export const METRIC_PROFIT_SHARE = 'Profit Share'
export const BASE_TIER_GROUP = 'Distributions under tier group'
export const SOURCE_FIXED = 'fixed'
export const SOURCE_REMAINDER = 'remainder'
// the check the engine runs for a waterfall that names none (wf__definition)
export const HISTORY_CHECK_DEFAULT = 'to_date'
// the tier type that offsets the other pools' realized losses (cfg.waterfall_tier_types)
export const TYPE_CROSS_POOL = 'Cross-Pool Loss Offset'
export const GP_NONE = -1

export interface TierRow {
  key: string
  id?: string
  tier_type_id: number | null
  stop_metric_id: number | null
  stop_base_id: number | null
  threshold_source_id: number | null
  stop_threshold: number | null
  stop_tier_group: string | null
  stop_recipient_class_id: number | null
  reference: string | null
  // the GP split: GP_NONE = no GP split on this tier
  gp_rate_source_id: number
  gp_split_percent: number | null
  gp_split_id?: string
  lp_split_id?: string
  /** a Cross-Pool Loss Offset tier: the pools it offsets against (none = every other pool) */
  pool_targets: string[]
}

/** A stored tier as the grid needs it (a fund waterfall tier or a template tier). */
export interface StoredTier {
  id: string
  tier_type_id: number
  stop_metric_id?: number | null
  stop_base_id?: number | null
  threshold_source_id?: number | null
  stop_threshold?: number | null
  stop_tier_group?: string | null
  stop_recipient_class_id?: number | null
  reference?: string | null
  pool_targets?: string[]
  splits: Array<{ id: string; recipient_class_id: number; rate_source_id?: number | null; split_percent?: number | null }>
}

let rowKeySeq = 0
export const nextRowKey = () => `waterfall-tier-${++rowKeySeq}`

/** A new, empty tier: stops on the LPs' receipts, no GP share. */
export const blankTierRow = (lpClassId: number | null): TierRow => ({
  key: nextRowKey(),
  tier_type_id: null,
  stop_metric_id: null,
  stop_base_id: null,
  threshold_source_id: null,
  stop_threshold: null,
  stop_tier_group: null,
  stop_recipient_class_id: lpClassId,
  reference: null,
  gp_rate_source_id: GP_NONE,
  gp_split_percent: null,
  pool_targets: []
})

/** A stored tier as a grid row: the GP split (if any) and the LP split's id. */
export function tierToRow(t: StoredTier, gpClassId: number | null, lpClassId: number | null): TierRow {
  const gp = t.splits.find((s) => s.recipient_class_id === gpClassId)
  const lp = t.splits.find((s) => s.recipient_class_id === lpClassId)
  return {
    key: t.id,
    id: t.id,
    tier_type_id: t.tier_type_id,
    stop_metric_id: t.stop_metric_id ?? null,
    stop_base_id: t.stop_base_id ?? null,
    threshold_source_id: t.threshold_source_id ?? null,
    stop_threshold: t.stop_threshold ?? null,
    stop_tier_group: t.stop_tier_group ?? null,
    stop_recipient_class_id: t.stop_recipient_class_id ?? null,
    reference: t.reference ?? null,
    gp_rate_source_id: gp?.rate_source_id ?? GP_NONE,
    gp_split_percent: gp?.split_percent ?? null,
    gp_split_id: gp?.id,
    lp_split_id: lp?.id,
    pool_targets: t.pool_targets ?? []
  }
}

const byId = <T extends { id: number }>(list: T[]) => new Map(list.map((x) => [x.id, x]))
const toOptions = (list: WaterfallLookup[]) => list.map((x) => ({ value: x.id, label: x.label }))

/** The waterfall cfg lookups from the global configs, with the ids, maps, options and predicates the editors share. */
export function useWaterfallLookups() {
  const bases: WaterfallBasis[] = useSelector((state: any) => state.globalConfigs?.waterfall_bases ?? [])
  const rateSources: WaterfallRateSource[] = useSelector((state: any) => state.globalConfigs?.waterfall_rate_sources ?? [])
  const tierTypes: WaterfallLookup[] = useSelector((state: any) => state.globalConfigs?.waterfall_tier_types ?? [])
  const stopMetrics: WaterfallLookup[] = useSelector((state: any) => state.globalConfigs?.waterfall_stop_metrics ?? [])
  const stopBases: WaterfallLookup[] = useSelector((state: any) => state.globalConfigs?.waterfall_stop_bases ?? [])
  const recipientClasses: WaterfallLookup[] = useSelector((state: any) => state.globalConfigs?.waterfall_recipient_classes ?? [])
  const scopes: WaterfallLookup[] = useSelector((state: any) => state.globalConfigs?.waterfall_scopes ?? [])
  const templates: WaterfallTemplate[] = useSelector((state: any) => state.globalConfigs?.waterfall_templates ?? [])
  const historyChecks: WaterfallHistoryCheck[] = useSelector(
    (state: any) => state.globalConfigs?.waterfall_history_checks ?? []
  )

  return useMemo(() => {
    const lpClassId = recipientClasses.find((c) => c.label === LP_CLASS)?.id ?? null
    const gpClassId = recipientClasses.find((c) => c.label === GP_CLASS)?.id ?? null
    const noneMetricId = stopMetrics.find((m) => m.label === METRIC_NONE)?.id ?? null
    const tierGroupBaseId = stopBases.find((b) => b.label === BASE_TIER_GROUP)?.id ?? null
    const fixedSourceId = rateSources.find((s) => s.code === SOURCE_FIXED)?.id ?? null
    const remainderSourceId = rateSources.find((s) => s.code === SOURCE_REMAINDER)?.id ?? null
    const metricsById = byId(stopMetrics)
    const basesById = byId(stopBases)
    const typesById = byId(tierTypes)
    const classesById = byId(recipientClasses)
    const sourcesById = byId(rateSources)
    const isFixed = (sourceId: number | null) => sourceId != null && sourceId === fixedSourceId
    const isNone = (row: TierRow) => row.stop_metric_id != null && row.stop_metric_id === noneMetricId
    const isTierGroup = (row: TierRow) => row.stop_base_id != null && row.stop_base_id === tierGroupBaseId
    const isProfitShare = (row: TierRow) => metricsById.get(row.stop_metric_id ?? -1)?.label === METRIC_PROFIT_SHARE
    const isCrossPool = (row: TierRow) => typesById.get(row.tier_type_id ?? -1)?.label === TYPE_CROSS_POOL
    return {
      bases,
      rateSources,
      tierTypes,
      stopMetrics,
      stopBases,
      recipientClasses,
      scopes,
      templates,
      historyChecks,
      lpClassId,
      gpClassId,
      noneMetricId,
      fixedSourceId,
      remainderSourceId,
      metricsById,
      basesById,
      typesById,
      classesById,
      sourcesById,
      isFixed,
      isNone,
      isTierGroup,
      isProfitShare,
      isCrossPool,
      typeOptions: toOptions(tierTypes),
      metricOptions: toOptions(stopMetrics),
      baseOptions: toOptions(stopBases),
      classOptions: toOptions(recipientClasses),
      scopeOptions: toOptions(scopes),
      basisOptions: bases.map((b) => ({ value: b.id, label: b.label })),
      historyCheckOptions: historyChecks.map((h) => ({ value: h.id, label: h.label })),
      defaultHistoryCheckId: historyChecks.find((h) => h.code === HISTORY_CHECK_DEFAULT)?.id ?? null,
      historyCheckHelp: lookupOptionsHelp(
        'How a new distribution treats the ones already made - whether carry already paid is measured again.',
        historyChecks
      ),
      thresholdSourceOptions: rateSources.filter((s) => s.applies_to !== 'rate').map((s) => ({ value: s.id, label: s.label })),
      gpSourceOptions: [
        { value: GP_NONE, label: 'No GP share' },
        ...rateSources
          .filter((s) => s.applies_to !== 'threshold' && s.code !== SOURCE_REMAINDER)
          .map((s) => ({ value: s.id, label: s.label }))
      ]
    }
  }, [bases, rateSources, tierTypes, stopMetrics, stopBases, recipientClasses, scopes, templates, historyChecks])
}

export type WaterfallLookups = ReturnType<typeof useWaterfallLookups>

/** The first row that cannot be sent (no type or no stop metric), 1-based; 0 when every row is complete. */
export const firstIncompleteTier = (rows: TierRow[]): number =>
  rows.findIndex((r) => r.tier_type_id == null || r.stop_metric_id == null) + 1

/**
 * The rows as the save payloads' tiers: a None tier carries no stop, a fixed
 * threshold its figure, the GP split when there is one, the LP split always the
 * remainder, and a Cross-Pool tier the pools it offsets against (withPools).
 */
export function rowsToTierPayloads(rows: TierRow[], lk: WaterfallLookups, withPools: boolean): WaterfallTierPayload[] {
  const gpClassId = lk.gpClassId as number
  const lpClassId = lk.lpClassId as number
  const remainderSourceId = lk.remainderSourceId as number
  return rows.map((r) => {
    const none = lk.isNone(r)
    const splits: WaterfallSplitPayload[] = []
    if (r.gp_rate_source_id !== GP_NONE) {
      splits.push({
        ...(r.gp_split_id ? { id: r.gp_split_id } : {}),
        recipient_class_id: gpClassId,
        rate_source_id: r.gp_rate_source_id,
        split_percent: lk.isFixed(r.gp_rate_source_id) ? r.gp_split_percent : null
      })
    }
    splits.push({
      ...(r.lp_split_id ? { id: r.lp_split_id } : {}),
      recipient_class_id: lpClassId,
      rate_source_id: remainderSourceId
    })
    return {
      ...(r.id ? { id: r.id } : {}),
      tier_type_id: r.tier_type_id as number,
      stop_metric_id: r.stop_metric_id as number,
      stop_base_id: none ? null : r.stop_base_id,
      threshold_source_id: none ? null : r.threshold_source_id,
      stop_threshold: none || !lk.isFixed(r.threshold_source_id) ? null : r.stop_threshold,
      stop_tier_group: none ? null : r.stop_tier_group || null,
      stop_recipient_class_id: none ? null : r.stop_recipient_class_id,
      reference: r.reference,
      ...(withPools && lk.isCrossPool(r) ? { pool_targets: r.pool_targets } : {}),
      splits
    }
  })
}

interface WaterfallTierGridProps {
  rows: TierRow[]
  onChange: (rows: TierRow[]) => void
  disabled: boolean
  lookups: WaterfallLookups
  /** the pools of a fund waterfall (the Offsets against column); none on a template */
  pools?: Array<{ id: string; pool_code: string }>
  /** the fund's default carry terms, printed beside each commitment-sourced figure */
  fundDefaults?: FundCarryDefaults | null
  /** what is printed under "As the agreement reads" */
  footer?: React.ReactNode
}

export default function WaterfallTierGrid({
  rows,
  onChange,
  disabled,
  lookups: lk,
  pools = [],
  fundDefaults = null,
  footer
}: WaterfallTierGridProps) {
  const updateRow = (key: string, patch: Partial<TierRow>) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  const removeRow = (key: string) => onChange(rows.filter((r) => r.key !== key))

  const poolOptions = pools.map((p) => ({ value: p.id, label: p.pool_code }))
  const hasCrossPoolTier = rows.some(lk.isCrossPool)

  const percentInput = (value: number | null, onValue: (v: number | null) => void, ariaLabel: string) => (
    <InputNumber
      size='small'
      className='w-full'
      value={decimalToPercent(value)}
      precision={2}
      suffix='%'
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(v) => onValue(percentToDecimal(v))}
    />
  )

  // the tier as the agreement states it (the wording is shared with the
  // distribution form's Tiers strip: waterfallTierWording.ts)
  const describeTier = (row: TierRow): string =>
    describeTierTerms({
      gpSource: row.gp_rate_source_id === GP_NONE ? null : lk.sourcesById.get(row.gp_rate_source_id) ?? null,
      gpSplitPercent: row.gp_split_percent,
      isNone: lk.isNone(row),
      metricLabel: lk.metricsById.get(row.stop_metric_id ?? -1)?.label,
      baseLabel: lk.basesById.get(row.stop_base_id ?? -1)?.label,
      stopClassLabel: lk.classesById.get(row.stop_recipient_class_id ?? -1)?.label,
      thresholdSource: lk.sourcesById.get(row.threshold_source_id ?? -1),
      stopThreshold: row.stop_threshold,
      stopTierGroup: row.stop_tier_group,
      isTierGroupBase: lk.isTierGroup(row),
      fundDefaults,
      ...(lk.isCrossPool(row)
        ? { lossOffsetPools: pools.filter((p) => row.pool_targets.includes(p.id)).map((p) => p.pool_code) }
        : {})
    })

  const columns = [
    {
      title: '#',
      dataIndex: 'sequence',
      width: 40,
      render: (_: unknown, __: TierRow, index: number) => index + 1
    },
    {
      title: 'Tier',
      dataIndex: 'tier_type_id',
      width: 150,
      render: (_: unknown, row: TierRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.tier_type_id}
          options={lk.typeOptions}
          placeholder='Tier type...'
          disabled={disabled}
          aria-label={`Tier type, tier ${rows.indexOf(row) + 1}`}
          onChange={(v) => updateRow(row.key, { tier_type_id: v })}
        />
      )
    },
    {
      title: 'Stop metric',
      dataIndex: 'stop_metric_id',
      width: 120,
      render: (_: unknown, row: TierRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.stop_metric_id}
          options={lk.metricOptions}
          placeholder='Metric...'
          disabled={disabled}
          aria-label={`Stop metric, tier ${rows.indexOf(row) + 1}`}
          onChange={(v) => updateRow(row.key, { stop_metric_id: v })}
        />
      )
    },
    {
      title: 'Stop base',
      dataIndex: 'stop_base_id',
      width: 190,
      render: (_: unknown, row: TierRow) => (
        <Select
          size='small'
          className='w-full'
          value={lk.isNone(row) ? null : row.stop_base_id}
          options={lk.baseOptions}
          placeholder='Base...'
          disabled={disabled || lk.isNone(row)}
          aria-label={`Stop base, tier ${rows.indexOf(row) + 1}`}
          onChange={(v) => updateRow(row.key, { stop_base_id: v })}
        />
      )
    },
    {
      title: (
        <Tooltip title='The tiers whose distributions count toward this test, as "n" or "n-m" (tier 1 of Origin counts only its own return of capital; the GP catch-up tests 25% of tiers 2-3). Blank = every tier. Required with the "Distributions under tier group" base.'>
          Tiers
        </Tooltip>
      ),
      dataIndex: 'stop_tier_group',
      width: 70,
      render: (_: unknown, row: TierRow) => (
        <Input
          size='small'
          value={lk.isNone(row) ? '' : (row.stop_tier_group ?? '')}
          placeholder='2-3'
          disabled={disabled || lk.isNone(row)}
          aria-label={`Tier group, tier ${rows.indexOf(row) + 1}`}
          onChange={(e) => updateRow(row.key, { stop_tier_group: e.target.value || null })}
        />
      )
    },
    {
      title: 'Threshold',
      dataIndex: 'threshold_source_id',
      width: 200,
      render: (_: unknown, row: TierRow) => (
        <Select
          size='small'
          className='w-full'
          value={lk.isNone(row) ? null : row.threshold_source_id}
          options={lk.thresholdSourceOptions}
          placeholder='Threshold from...'
          disabled={disabled || lk.isNone(row)}
          aria-label={`Threshold source, tier ${rows.indexOf(row) + 1}`}
          onChange={(v) => updateRow(row.key, { threshold_source_id: v })}
        />
      )
    },
    {
      title: 'Figure',
      dataIndex: 'stop_threshold',
      width: 90,
      render: (_: unknown, row: TierRow) => {
        const active = !lk.isNone(row) && lk.isFixed(row.threshold_source_id)
        if (!active) return <Text type='secondary'>—</Text>
        return lk.isProfitShare(row) ? (
          percentInput(row.stop_threshold, (v) => updateRow(row.key, { stop_threshold: v }), `Threshold, tier ${rows.indexOf(row) + 1}`)
        ) : (
          <InputNumber
            size='small'
            className='w-full'
            value={row.stop_threshold}
            min={0}
            suffix='×'
            disabled={disabled}
            aria-label={`Threshold, tier ${rows.indexOf(row) + 1}`}
            onChange={(v) => updateRow(row.key, { stop_threshold: v == null ? null : Number(v) })}
          />
        )
      }
    },
    {
      title: 'Stop on',
      dataIndex: 'stop_recipient_class_id',
      width: 90,
      render: (_: unknown, row: TierRow) => (
        <Select
          size='small'
          className='w-full'
          value={lk.isNone(row) ? null : row.stop_recipient_class_id}
          options={lk.classOptions}
          placeholder='LP / GP'
          disabled={disabled || lk.isNone(row)}
          aria-label={`Stop on, tier ${rows.indexOf(row) + 1}`}
          onChange={(v) => updateRow(row.key, { stop_recipient_class_id: v })}
        />
      )
    },
    {
      title: 'GP share',
      dataIndex: 'gp_rate_source_id',
      width: 200,
      render: (_: unknown, row: TierRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.gp_rate_source_id}
          options={lk.gpSourceOptions}
          disabled={disabled}
          aria-label={`GP share, tier ${rows.indexOf(row) + 1}`}
          onChange={(v) => updateRow(row.key, { gp_rate_source_id: v })}
        />
      )
    },
    {
      title: 'GP %',
      dataIndex: 'gp_split_percent',
      width: 90,
      render: (_: unknown, row: TierRow) =>
        lk.isFixed(row.gp_rate_source_id) ? (
          percentInput(row.gp_split_percent, (v) => updateRow(row.key, { gp_split_percent: v }), `GP percent, tier ${rows.indexOf(row) + 1}`)
        ) : (
          <Text type='secondary'>—</Text>
        )
    },
    // a waterfall with a Cross-Pool Loss Offset tier: the pools it offsets against
    ...(hasCrossPoolTier
      ? [
          {
            title: (
              <span className='whitespace-nowrap'>
                Offsets against
                <HelpTip title='The pools whose realized losses this tier offsets. Leave empty for every other pool.' />
              </span>
            ),
            dataIndex: 'pool_targets',
            width: 200,
            render: (_: unknown, row: TierRow) =>
              lk.isCrossPool(row) ? (
                <Select
                  size='small'
                  className='w-full'
                  style={{ minWidth: 170 }}
                  popupMatchSelectWidth={false}
                  mode='multiple'
                  allowClear
                  value={row.pool_targets}
                  options={poolOptions}
                  placeholder='All other pools'
                  disabled={disabled || pools.length === 0}
                  aria-label={`Offsets against, tier ${rows.indexOf(row) + 1}`}
                  onChange={(v: string[]) => updateRow(row.key, { pool_targets: v })}
                />
              ) : (
                <Text type='secondary'>—</Text>
              )
          }
        ]
      : []),
    {
      title: 'Reference',
      dataIndex: 'reference',
      width: 110,
      render: (_: unknown, row: TierRow) => (
        <Input
          size='small'
          value={row.reference ?? ''}
          placeholder='§6.4(a)(i)'
          disabled={disabled}
          aria-label={`Reference, tier ${rows.indexOf(row) + 1}`}
          onChange={(e) => updateRow(row.key, { reference: e.target.value || null })}
        />
      )
    },
    ...(disabled
      ? []
      : [
          {
            title: '',
            dataIndex: 'actions',
            width: 40,
            render: (_: unknown, row: TierRow) => (
              <Button
                type='text'
                size='small'
                danger
                icon={<DeleteOutlined />}
                aria-label={`Remove tier ${rows.indexOf(row) + 1}`}
                onClick={() => removeRow(row.key)}
              />
            )
          }
        ])
  ]

  if (rows.length === 0) {
    return <Text type='secondary'>No tiers — add the return of capital first, then the splits.</Text>
  }

  return (
    <>
      <Table size='small' columns={columns} dataSource={rows} pagination={false} rowKey='key' scroll={{ x: hasCrossPoolTier ? 1500 : 1300 }} />
      <div className='mt-3'>
        <Text type='secondary'>As the agreement reads</Text>
        <ol className='list-decimal pl-5 mt-1 mb-0'>
          {rows.map((row, index) => (
            <li key={row.key} data-testid={`tier-reads-${index + 1}`}>
              <Text>{describeTier(row)}</Text>
              {row.reference ? <Text type='secondary'> ({row.reference})</Text> : null}
            </li>
          ))}
        </ol>
        {footer}
      </div>
    </>
  )
}
